/**
 * Module: api/creator
 * Role: Typed wrappers for buyer creator-role HTTP endpoints (FTR-1106).
 *       Mirrors `src/api/v2/buyer/_creator_schemas.py` shapes manually (L-240:
 *       inline TS/Zod schemas, never import from api/types.ts — keeps each
 *       miniapp module self-describing and survives backend refactors).
 *
 *       Each response is parsed through a Zod schema before returning so we
 *       fail loud (with a precise issue path) when backend drifts vs the
 *       Pydantic source-of-truth, instead of crashing in a far-away component.
 *
 * Uses: ./client:apiFetch, zod
 * Used by:
 *   - screens/creator/* (wizard screens)
 *   - screens/ProfileScreen.tsx (creator status row)
 *   - screens/HomeScreen/* (promo CTA)
 *   - screens/TasksScreen.tsx (banner CTA)
 *
 * NOTE: TypeScript types below are a manual mirror of the Pydantic source-of-truth
 * at src/api/v2/buyer/_creator_schemas.py. Keep in sync when the Python models change.
 */

import { z } from 'zod';
import { apiFetch } from './client';

// ---------------------------------------------------------------------------
// Zod schemas — manual mirror of src/api/v2/buyer/_creator_schemas.py
// ---------------------------------------------------------------------------

const CreatorApplicationStatusEnum = z.enum([
  'pending',
  'pending_scraping',
  'approved',
  'rejected',
  'needs_info',
  'banned',
]);

const VerificationMethodEnum = z.enum([
  'vision_ocr',
  'vk_api',
  'vk_api+vision',
  'manual',
]);

const CreatorApplicationSchema = z.object({
  id: z.number().int(),
  buyer_id: z.string(), // UUID — backend serializes as string
  status: CreatorApplicationStatusEnum,
  portfolio_url: z.string(),
  sample_urls: z.array(z.string()).nullable(),
  bio: z.string().nullable(),
  application_data: z.record(z.string(), z.unknown()).nullable(),
  rejection_reason: z.string().nullable(),
  reviewed_by: z.string().nullable(),
  reviewed_at: z.string().nullable(), // ISO-8601
  created_at: z.string(),
  updated_at: z.string(),
  selfie_url: z.string().nullable(),
  vk_url: z.string().nullable(),
  tiktok_url: z.string().nullable(),
  parsed_follower_count_vk: z.number().int().nullable(),
  parsed_follower_count_tiktok: z.number().int().nullable(),
  verification_confidence: z.number().nullable(),
  verification_method: VerificationMethodEnum.nullable(),
  selfie_verified_at: z.string().nullable(),
  social_api_verified_at: z.string().nullable(),
  avg_views_30d_vk: z.number().int().nullable(),
  avg_views_90d_vk: z.number().int().nullable(),
});

const CreatorStateSchema = z.object({
  account_kind: z.enum(['standard', 'creator']),
  application: CreatorApplicationSchema.nullable(),
  can_apply_at: z.string().nullable(),
  // FTR-1106 Task 3: mirrors _creator_schemas.py:CreatorState.creator_profile_complete.
  // Default false for backward compat (old backend versions won't return this field).
  creator_profile_complete: z.boolean().default(false),
});

const CreatorVerifyStatusViewSchema = z.object({
  status: z.string(), // 'no_application' | CreatorApplicationStatus
  application_id: z.number().int().nullable(),
  portfolio_url: z.string().nullable(),
  rejection_reason: z.string().nullable(),
  selfie_url: z.string().nullable(),
  vk_url: z.string().nullable(),
  tiktok_url: z.string().nullable(),
  parsed_follower_count_vk: z.number().int().nullable(),
  parsed_follower_count_tiktok: z.number().int().nullable(),
  verification_confidence: z.number().nullable(),
  verification_method: z.string().nullable(),
  selfie_verified_at: z.string().nullable(),
  social_api_verified_at: z.string().nullable(),
  avg_views_30d_vk: z.number().int().nullable(),
  avg_views_90d_vk: z.number().int().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

const CreatorOptionsLimitsSchema = z.object({
  max_formats: z.number().int(),
  max_cities: z.number().int(),
  city_max_chars: z.number().int(),
});

const CreatorOptionsResponseSchema = z.object({
  categories: z.array(z.string()),
  formats: z.array(z.string()),
  genders: z.array(z.string()),
  age_ranges: z.array(z.string()),
  limits: CreatorOptionsLimitsSchema,
});

const CreatorVerifyResponseSchema = z.object({
  application_id: z.number().int(),
  status: z.enum(['pending_scraping', 'rejected']),
  rejection_reason: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Inferred TS types (single source — `z.infer` from the schemas above)
// ---------------------------------------------------------------------------

export type CreatorApplicationStatus = z.infer<typeof CreatorApplicationStatusEnum>;
export type CreatorVerificationMethod = z.infer<typeof VerificationMethodEnum>;
export type CreatorApplication = z.infer<typeof CreatorApplicationSchema>;
export type CreatorState = z.infer<typeof CreatorStateSchema>;
export type CreatorVerifyStatusView = z.infer<typeof CreatorVerifyStatusViewSchema>;
export type CreatorOptionsLimits = z.infer<typeof CreatorOptionsLimitsSchema>;
export type CreatorOptionsResponse = z.infer<typeof CreatorOptionsResponseSchema>;
export type CreatorVerifyResponse = z.infer<typeof CreatorVerifyResponseSchema>;

/**
 * POST /api/v2/buyer/creator/verify_apply request body.
 * At least one of vk_url / tiktok_url is required (enforced server-side).
 *
 * Kept as a hand-written interface (not z.infer) because the request shape
 * has optional/conditional fields with cross-field validation handled by
 * React Hook Form + the per-step Zod form schema (Task 3).
 */
export interface CreatorVerifyRequest {
  selfie_base64: string;
  vk_url?: string;
  tiktok_url?: string;
  screenshot_base64?: string;
  profile_data?: Record<string, unknown>;
}

/**
 * Custom Error subclass for verify_apply rate-limit (HTTP 429).
 * Carries the parsed Retry-After header (seconds) so screens can render
 * "Повторите попытку через X сек." without re-parsing the response.
 */
export class CreatorVerifyRateLimitError extends Error {
  readonly status = 429;
  readonly retryAfter: number | null;

  constructor(retryAfter: number | null) {
    super(
      retryAfter !== null
        ? `creator/verify_apply rate-limited (retry in ${retryAfter}s)`
        : 'creator/verify_apply rate-limited',
    );
    this.name = 'CreatorVerifyRateLimitError';
    this.retryAfter = retryAfter;
  }
}

// ---------------------------------------------------------------------------
// API wrappers
// ---------------------------------------------------------------------------

/** GET /api/v2/buyer/creator/status — current account_kind + application snapshot. */
export async function getCreatorStatus(): Promise<CreatorState> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/status');
  return CreatorStateSchema.parse(raw);
}

/**
 * GET /api/v2/buyer/creator/options — wizard option lists + limits (SSOT).
 *
 * Use staleTime: Infinity in React Query — options are pure enum, never change at runtime.
 */
export async function getCreatorOptions(): Promise<CreatorOptionsResponse> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/options');
  return CreatorOptionsResponseSchema.parse(raw);
}

/**
 * POST /api/v2/buyer/creator/verify_apply — submit selfie + social URL for verification.
 *
 * On HTTP 429 (rate-limit, 1/24h), throws `CreatorVerifyRateLimitError` with
 * `retryAfter` (seconds, may be null if header missing). All other errors
 * bubble up as the generic Error & {status} from apiFetch.
 */
export async function submitVerifyApply(
  req: CreatorVerifyRequest,
): Promise<CreatorVerifyResponse> {
  try {
    const raw = await apiFetch<unknown>('/api/v2/buyer/creator/verify_apply', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return CreatorVerifyResponseSchema.parse(raw);
  } catch (err) {
    const e = err as Error & { status?: number; retryAfter?: number | null };
    if (e.status === 429) {
      throw new CreatorVerifyRateLimitError(e.retryAfter ?? null);
    }
    throw err;
  }
}

/** GET /api/v2/buyer/creator/verify_status — lightweight status poll. */
export async function getVerifyStatus(): Promise<CreatorVerifyStatusView> {
  const raw = await apiFetch<unknown>('/api/v2/buyer/creator/verify_status');
  return CreatorVerifyStatusViewSchema.parse(raw);
}
